import React from 'react'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'

const ignoredReactErrorMessages = ['ResizeObserver loop']

const shouldIgnore = (event) =>
  event.errors.some((error) => ignoredReactErrorMessages.some((msg) => error.errorMessage.includes(msg)))

Bugsnag.start({
  apiKey: process.env.NEXT_PUBLIC_BUGSNAG_API_KEY || '',
  plugins: [new BugsnagPluginReact(React)],
  releaseStage: process.env.NODE_ENV,
  enabledReleaseStages: ['production', 'staging'],
  onError: (event) => {
    // Ignore events with specific error messages to avoid logging known non-actionable React errors.
    if (shouldIgnore(event)) {
      return false
    }

    return true
  },
})

// Fallback error boundary used when Bugsnag's React plugin fails to initialize
// (e.g. missing API key, SSR context). Prevents <undefined> being used as a JSX component
// which would itself cause an uncaught crash in _app.tsx.
class FallbackErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean }> {
  constructor(props: { children: React.ReactNode }) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError() {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    try {
      Bugsnag.notify(error, (event) => {
        event.addMetadata('react', { componentStack: info.componentStack })
      })
    } catch {
      // Bugsnag not available — swallow silently
    }
  }

  render() {
    if (this.state.hasError) {
      return null
    }
    return this.props.children as React.ReactElement
  }
}

export default Bugsnag
export const BugsnagErrorBoundary = Bugsnag.getPlugin('react')?.createErrorBoundary() ?? FallbackErrorBoundary
